<template>
  <!-- eslint-disable max-len -->
  <div ref="page" class="container">
    <div class="content">
      <div class="flex justify-between mb-32 gap-24">
        <h2 class="hdg hdg--2 mb-8">Filters</h2>
        <div class="flex flex-1 md-1:flex-auto justify-between md-1:justify-end gap-24">
          <button v-if="showResetButton" class="text-s font-medium underline primary-focus" @click.prevent="resetForm">
            Reset
          </button>
          <button class="md-1:hidden primary-focus ml-auto" @click="close"><close-icon color="#4B4C52" /></button>
        </div>
      </div>
      <form class="search__form" @submit.prevent="submitForm">
        <div class="form__section form__group--border">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Looking for</h2>
          </div>
          <poppins-dropdown
            id="careType"
            v-model="currentCare"
            class="mb-24"
            labelName="Care type"
            name="care-type"
            :options="modifiedCareTypeOptions"
          />
        </div>

        <div class="form__section form__group--border">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Where</h2>
          </div>
          <div class="flex gap-16 w-full">
            <poppins-text-field
              id="zipcode"
              v-model="zipcode"
              labelName="ZIP code"
              name="postal-code"
              type="text"
              maxlength="5"
              pattern="[0-9]*"
              class="mb-24 w-full"
              inputmode="numeric"
              aria-label="Five-digit zip code"
              :status="zipcodeStatus"
              :helperText="zipcodeHelperText"
              :limit-input-to-pattern="true"
              :showActiveState="true"
              @blur="handleTextFieldBlur"
              @keydownEnter="submitForm"
            >
              <template #icon><search-outline-icon color="#4B4C52" /></template>
            </poppins-text-field>
            <poppins-dropdown
              id="form-distance"
              v-model="distance"
              labelName="Within"
              class="mb-24 w-full"
              :options="distanceOptions"
            />
          </div>
        </div>

        <div class="form__section form__group--border pb-16">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Age range</h2>
          </div>
          <poppins-checkbox
            v-for="option in ageRangeOptions"
            :key="option.value"
            v-model="ageRanges"
            :value="option.value"
            :checkbox-id="`form-age-range-${option.value.toLowerCase()}`"
            :labelName="option.label"
            labelClass="mb-16"
          />
        </div>

        <div class="form__section">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Rate</h2>
          </div>
          <div class="form__group form__group--border">
            <div class="form__group-item">
              <legend class="form__label">Maximum</legend>
              <div><poppins-rate v-model="rate" /></div>
            </div>
          </div>
        </div>

        <div class="form__section form__group--border">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Experience</h2>
          </div>

          <poppins-dropdown
            id="form-experience"
            v-model="yearsExperience"
            labelName="Minimum years experience"
            class="mb-24"
            :options="experienceOptions"
          />
        </div>

        <!-- <div class="form__section form__group--border">
          <div class="heading-bar--thin">
            <h2 class="hdg hdg--4">When</h2>
            <p class="text-small mt-16">For upcoming event, date night, or backup care</p>
          </div>
          <poppins-date-picker
            id="form-datepicker"
            v-model="startDate"
            labelName="Date"
            class="mb-16"
            customClasses="w-full"
            hasIcon
          />
          <div class="flex gap-16 mb-24">
            <poppins-time-picker
              id="form-start-date"
              v-model="start"
              class="w-full"
              labelName="Start time"
              :start="startRange[0]"
              :end="startRange[1]"
              :status="startTimeStatus"
              :helperText="startTimeHelperText"
            />
            <poppins-time-picker
              id="regular-schedule-end"
              v-model="end"
              class="w-full"
              labelName="End time"
              :start="endRange[0]"
              :end="endRange[1]"
              :status="endTimeStatus"
              :helperText="endTimeHelperText"
            />
          </div>
        </div> -->

        <div class="form__section form__group--border">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Requirements</h2>
          </div>

          <poppins-checkbox
            v-for="option in requirementOptions"
            :key="option.value"
            v-model="requirements"
            :value="option.value"
            :checkbox-id="`form-requirement-${option.value.toLowerCase()}`"
            :labelName="option.label"
            labelClass="mb-16"
          />

          <LanguageDropdown :inputLanguages="languages" @updateLanguages="languages = $event" />
        </div>

        <div class="form__section form__group--border pb-16">
          <div class="heading-bar-sm">
            <h2 class="hdg hdg--4">Responsibilities</h2>
          </div>
          <poppins-checkbox
            v-for="option in responsibilityOptions"
            :key="option.value"
            v-model="responsibilities"
            :value="option.value"
            :checkbox-id="`form-responsibility-${option.value.toLowerCase()}`"
            :labelName="option.label"
            labelClass="mb-16"
          />
        </div>

        <div class="form__section">
          <div class="heading-bar--thin">
            <h2 class="hdg hdg--4">More</h2>
          </div>
          <legend class="form__label heading-bar--thin">Background checks</legend>
          <poppins-checkbox
            v-for="option in backgroundCheckOptions"
            :key="option.value"
            v-model="backgroundChecks"
            :value="option.value"
            :checkbox-id="`form-background-check-${option.value.toLowerCase()}`"
            :labelName="option.label"
            labelClass="mb-16"
          />

          <RadioButtonGroup
            v-model="lastLoginDays"
            class="my-32"
            name="Last login"
            :options="lastLoginOptions"
            :defaultValue="defaultLastLoginDays()"
          />
        </div>

        <div class="form__section heading-bar-sm">
          <div class="heading-bar-sm"></div>
          <poppins-text-field
            id="keywords"
            v-model="keywords"
            labelName="Keyword search"
            name="keywords"
            type="text"
            class="mb-24"
            aria-label="keywords"
            :showActiveState="true"
            @blur="handleTextFieldBlur"
            @keydownEnter="submitForm"
          />
        </div>

        <StickyButton
          ref="input"
          class="sm:!bg-transparent sm:!border-t-0 sm:!shadow-none sm:p-0 mb-80 sm:mb-0 md-1:hidden"
          variant="alwaysSticky"
          value="Filter"
        />
      </form>
    </div>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script lang="ts">
import { v4 as uuidv4 } from 'uuid';
import { add, set, intervalToDuration, startOfDay, endOfDay, formatISO } from 'date-fns';
import debounce from 'lodash/debounce';
import { validate } from '@/lib/search/form_utils';
import helperStatus from '@/helpers/status';
import LanguageDropdown from '@/apps/search/LanguageDropdown.vue';

// Components
import Checkbox from '@/components/checkbox/Checkbox.vue';
import Dropdown from '@/components/dropdown/Dropdown.vue';
// import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import PoppinsRate from '@/components/counterRate/PoppinsRate.vue';
// import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import RadioButtonGroup from '@/components/radioButtonGroup/RadioButtonGroup.vue';
import StickyButton from '@/components/sticky_button/StickyButton.vue';
import TextField from '@/components/textfield/TextField.vue';
import CloseIcon from '@/graphics/icons/CloseIcon.vue';
import SearchOutlineIcon from '@/graphics/icons/SearchOutlineIcon.vue';

const distanceOptions = [
  { value: 1, display: '1 mi' },
  { value: 2, display: '2 mi' },
  { value: 5, display: '5 mi' },
  { value: 10, display: '10 mi' },
  { value: 15, display: '15 mi' },
  { value: 25, display: '25 mi' },
  { value: 40, display: '40 mi' },
  { value: 50, display: '50 mi' },
];
const ageRangeOptions = [
  { value: 'FIRST_YEAR', label: 'Infant (0-11 months)' },
  { value: 'TODDLERS', label: 'Toddler (1-2 years)' },
  { value: 'PRESCHOOLERS', label: 'Preschool (3-5 years)' },
  { value: 'GRADE_SCHOOL_AGE', label: 'Elementary (6-10 years)' },
  { value: 'PRE_TEEN_TEENAGERS', label: 'Middle School (11+ years)' },
];
const experienceOptions = ['Any', 1, 3, 5, 10];
const lastLoginOptions = [
  { value: 2, label: 'Within the last 48 hours' },
  { value: 7, label: 'Within the last week' },
  { value: 21, label: 'Within the last 3 weeks' },
  { value: 42, label: 'Within the last 6 weeks' },
  { value: 90, label: 'Within the last 3 months' },
];
const requirementOptions = [
  { value: 'HAS_CAR', label: 'Has their own car' },
  { value: 'COMFORTABLE_WITH_PETS', label: 'Comfortable with pets' },
  { value: 'MULTIPLES_EXPERIENCE', label: 'Multiples experience' },
  { value: 'SPECIAL_NEEDS_EXPERIENCE', label: 'Special needs experience' },
];
const responsibilityOptions = [
  { value: 'MEAL_PREPARATION', label: 'Preparing meals/snacks' },
  { value: 'LIGHT_HOUSEKEEPING', label: 'Light housekeeping' },
  { value: 'DRIVING_KIDS', label: 'Driving the kids' },
  { value: 'LAUNDRY', label: 'Light laundry' },
  { value: 'POTTY_TRAINING', label: 'Toilet training' },
  { value: 'HOMEWORK_HELP', label: 'Help with homework' },
  { value: 'SICK_CARE', label: 'Sick care' },
  { value: 'RUN_ERRANDS', label: 'Running errands' },
];
const backgroundCheckOptions = [
  { value: 'ENHANCED', label: 'Enhanced Criminal' },
  { value: 'BASIC', label: 'Basic Criminal' },
  { value: 'MOTOR', label: 'Motor Vehicle' },
];

export default {
  name: 'SearchForm',
  components: {
    LanguageDropdown,
    PoppinsTextField: TextField,
    PoppinsDropdown: Dropdown,
    PoppinsCheckbox: Checkbox,
    // PoppinsDatePicker,
    PoppinsRate,
    // PoppinsTimePicker,
    StickyButton,
    RadioButtonGroup,
    CloseIcon,
    SearchOutlineIcon,
  },
  props: {
    accountType: {
      type: String,
      required: true,
    },
  },
  data(): any {
    return {
      buttonClass: '',
      distance: (this as any).defaultDistance(),
      errors: {},
      rate: (this as any).defaultRate(),
      startDate: (this as any).defaultStartDate(),
      start: (this as any).startTimeFromParams(),
      end: (this as any).endTimeFromParams(),
      startRange: [],
      endRange: [],
      yearsExperience: (this as any).defaultYearsExperience(),
      userEnteredZipcode: undefined,
      languages: (this as any).defaultLanguages(),
      distanceOptions,
      experienceOptions,
      ageRangeOptions,
      requirementOptions,
      responsibilityOptions,
      backgroundCheckOptions,
      keywords: this.$route.query.keywords,
      backgroundChecks: (this as any).defaultBackgroundChecks(),
      ageRanges: (this as any).defaultAgeRanges(),
      requirements: (this as any).defaultRequirements(),
      responsibilities: (this as any).defaultResponsibilities(),
      lastLoginDays: (this as any).defaultLastLoginDays(),
      selectedCareType: 'Child care',
      currentCare: 'Child care',
      lastLoginOptions,
      isDesktop: window.innerWidth >= 768,
      debouncedAutoSubmit: null as any,
      isResetting: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    zipcode: {
      get() {
        // Always prioritize user input, but fall back to other sources only if user hasn't entered anything
        if (this.userEnteredZipcode !== undefined && this.userEnteredZipcode !== null) {
          return this.userEnteredZipcode;
        }

        return this.$route.query.zipcode || this.account?.location?.zipcode || '';
      },
      set(value) {
        this.userEnteredZipcode = value;
      },
    },
    modifiedCareTypeOptions() {
      const baseOptions = [
        { value: 'Child care', display: 'Child care' },
        { value: 'Adventures', display: 'Adventures' },
        { value: 'Special needs', display: 'Special needs' },
        { value: 'Companion care', display: 'Care companion' },
        { value: 'Pet care', display: 'Pet care' },
      ];

      if (this.accountType === 'corporate') {
        baseOptions.push({ value: 'Housekeeping', display: 'Housekeepers' });
      }

      return baseOptions;
    },
    zipcodeStatus(): helperStatus {
      if (this.zipcodeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    zipcodeHelperText() {
      const zipCodeRegex = /^\d{5}$/;
      if (this.zipcode && !zipCodeRegex.test(this.zipcode)) {
        return 'Please enter a five digit zip code';
      }
      return '';
    },
    startTimeStatus(): helperStatus {
      if (this.startTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    endTimeStatus(): helperStatus {
      if (this.endTimeHelperText) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    startTimeHelperText(): String {
      if (this.end && !this.start) {
        return 'This field is required';
      }
      return '';
    },
    endTimeHelperText(): String {
      if (this.start && !this.end) {
        return 'This field is required';
      }
      if (this.end && this.end < this.start) {
        return 'Choose an end time later than the start time.';
      }
      return '';
    },
    filterValues() {
      return {
        distance: this.distance,
        rate: this.rate,
        yearsExperience: this.yearsExperience,
        requirements: this.requirements,
        responsibilities: this.responsibilities,
        backgroundChecks: this.backgroundChecks,
        ageRanges: this.ageRanges,
        languages: this.languages,
        lastLoginDays: this.lastLoginDays,
      };
    },
    showResetButton(): boolean {
      // Show reset button if any filter has changed from default values
      return (
        this.distance !== 15 ||
        this.rate !== 20 ||
        this.yearsExperience !== 'Any' ||
        (this.keywords && this.keywords !== '') ||
        (this.requirements && this.requirements.length > 0) ||
        (this.responsibilities && this.responsibilities.length > 0) ||
        (this.backgroundChecks && this.backgroundChecks.length > 0) ||
        (this.ageRanges && this.ageRanges.length > 0) ||
        (this.languages && this.languages.length > 0) ||
        this.lastLoginDays !== 42 ||
        this.start !== null ||
        this.end !== null
      );
    },
  },
  watch: {
    startDate(day: Date) {
      if (!day || this.start > this.end) {
        this.start = null;
        this.end = null;
      }

      if (this.start) {
        const oldStart = this.start;
        this.start = set(this.start, {
          year: day.getFullYear(),
          month: day.getMonth(),
          date: day.getDate(),
        });

        if (this.end) {
          const diff = intervalToDuration({ start: oldStart, end: this.end });
          this.end = add(this.start, diff);
        }
      }
      this.calculateStartRange();
    },
    start(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.calculateEndRange();
      }
    },
    currentCare(newValue) {
      if (newValue) {
        this.generateProfileLink(newValue);
      }
    },
    filterValues: {
      handler() {
        if (this.isDesktop && !this.isResetting) {
          this.autoSubmitForm();
        }
      },
      deep: true,
    },
  },
  created() {
    this.calculateStartRange();
    this.calculateEndRange();
    // Caretype filter was removed so we want to remove it if it's in the query params
    this.removeCareTypeFromQuery();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.debouncedAutoSubmit = debounce(() => {
      this.submitForm();
    }, 300);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.debouncedAutoSubmit && this.debouncedAutoSubmit.cancel) {
      this.debouncedAutoSubmit.cancel();
    }
  },
  methods: {
    populateFiltersFromJob(filters: any) {
      // Prevent auto-submit during population
      this.isResetting = true;

      try {
        const arrayFields = ['ageRanges', 'languages', 'responsibilities'];
        arrayFields.forEach((field) => {
          if (filters[field]) {
            this[field] = Array.isArray(filters[field]) ? filters[field] : [filters[field]];
          }
        });

        if (filters.zipcode) {
          this.zipcode = filters.zipcode;
        }

        if (filters.rate !== null && filters.rate !== undefined) {
          const value = parseInt(filters.rate, 10);
          this.rate = Math.round(value / 100) || 20;
        }

        if (filters.requirements) {
          const requirementMapping = {
            hasCar: 'HAS_CAR',
            isComfortableWithPets: 'COMFORTABLE_WITH_PETS',
            hasMultiplesExperience: 'MULTIPLES_EXPERIENCE',
            hasSpecialNeedsExperience: 'SPECIAL_NEEDS_EXPERIENCE',
          };
          this.requirements = Object.entries(requirementMapping)
            .filter(([key]) => filters.requirements[key])
            .map(([, value]) => value);
        }

        this.$nextTick(() => {
          this.isResetting = false;
          this.submitForm();
        });
      } catch (error) {
        console.error('Error populating filters from job:', error);
        this.isResetting = false;
      }
    },
    submitForm() {
      if (this.validateForm()) {
        const vars: any = {
          zipcode: this.zipcode,
          sid: this.$route.query.sid || uuidv4(),
          distance: this.distance.toString(),
          rate: this.rate.toString(),
          keywords: this.keywords,
        };

        if (this.start && this.end) {
          vars.start = formatISO(this.start);
          vars.end = formatISO(this.end);
        }

        // Extract individual requirements from requirements array
        if (this.requirements) {
          if (this.requirements.includes('HAS_CAR')) {
            vars.hasCar = 'true';
          }
          if (this.requirements.includes('COMFORTABLE_WITH_PETS')) {
            vars.isComfortableWithPets = 'true';
          }
          if (this.requirements.includes('MULTIPLES_EXPERIENCE')) {
            vars.hasMultiplesExperience = 'true';
          }
          if (this.requirements.includes('SPECIAL_NEEDS_EXPERIENCE')) {
            vars.hasSpecialNeedsExperience = 'true';
          }
        }

        if (this.backgroundChecks) {
          vars.backgroundChecks = this.backgroundChecks;
        }

        if (this.ageRanges) {
          vars.ageRanges = this.ageRanges;
        }

        if (this.responsibilities && this.responsibilities.length > 0) {
          vars.responsibilities = this.responsibilities;
        }

        if (this.languages) {
          vars.languages = this.languages;
        }

        const ye = parseInt(this.yearsExperience, 10);
        if (ye) vars.yearsExperience = ye.toString();

        if (this.lastLoginDays) {
          vars.lastLoginDays = this.lastLoginDays;
        }

        this.$router.push({ query: vars });
      }
    },
    generateProfileLink(value: string) {
      if (!value) {
        return;
      }
      this.selectedCareType = value.toLowerCase().replace(/\s/g, '_');
      let link = '';
      if (this.selectedCareType === 'adventures') {
        link = `/adventures/search?zipcode=${this.zipcode}&careType=adventures`;
      } else if (this.selectedCareType === 'child_care') {
        link = `/availability/search?zipcode=${this.zipcode}&careType=both`;
      } else if (['child_care', 'babysitter', 'nanny', 'both'].indexOf(this.selectedCareType) === -1) {
        link = `/profile/provider/search?search_strategy=${this.selectedCareType}&zipcode=${this.zipcode}`;
      }
      if (link) {
        window.location.href = link;
      }
    },
    validateForm(): boolean {
      const params: any = {
        zipcode: this.zipcode,
        distance: this.distance,
        yearsExperience: parseInt(this.yearsExperience, 10),
        rate: this.rate,
      };

      this.errors = validate(params);
      /**
       * we have to display validation both on input and on submit
       * but also we have to be able to search if there are no start and end values
       * so we use both computed properties and this validation function
       * noEndTimeError and noStartTimeError are always true if there are no start and end values
       * but helper text only appears if the user has interacted with the component
       */
      if (this.startTimeHelperText) {
        this.errors.start = this.startTimeHelperText;
      }
      if (this.endTimeHelperText) {
        this.errors.end = this.endTimeHelperText;
      }
      /**
       * if there is no zipcode, we run default search
       * but if the user typed the zipcode into the url correctly, we want to validate
       */
      if (this.zipcodeHelperText) {
        this.errors.zipcode = this.zipcodeHelperText;
      }

      return Object.keys(this.errors).length === 0;
    },
    validateTimeParam(param?: string | string[]): Date | null {
      let p: string;

      if (!param) return null;

      if (param instanceof Array) [p] = param;
      else p = param;

      const dateFromParam = new Date(p);
      // eslint-disable-next-line
      const paramIsNumeric = !isNaN(dateFromParam.valueOf());

      if (dateFromParam instanceof Date && paramIsNumeric) {
        return dateFromParam;
      }

      return null;
    },
    close() {
      this.$emit('close');
    },
    /**
     * startDate, startRange, and endRange are not relevant for the search
     * they only exist for PoppinsDatePicker and PoppinsTimePicker to work correctly
     */
    defaultStartDate(): Date {
      const timeFromParamsOrDayAfterTomorrow =
        this.validateTimeParam(this.$route.query.start) || startOfDay(add(new Date(), { days: 2 }));

      return timeFromParamsOrDayAfterTomorrow;
    },
    calculateStartRange() {
      let d: Date;
      if (this.startDate) {
        d = this.startDate;
      } else {
        d = new Date();
      }

      const s = startOfDay(d);
      const e = endOfDay(d);

      this.startRange = [s, e];
    },
    calculateEndRange() {
      /**
       * !this.end is required to generate options when start time is picked
       * this.start < this.end is required to get range values from params correctly
       */

      if (!this.end || this.start < this.end) {
        const s = add(this.start, { minutes: 30 });
        const e = add(this.start, { days: 1 });

        this.endRange = [s, e];
      }
    },
    startTimeFromParams(): Date | null {
      const timeFromParams = this.validateTimeParam(this.$route.query.start);

      return timeFromParams || null;
    },
    endTimeFromParams(): Date | null {
      const endTimeFromParams = this.validateTimeParam(this.$route.query.end);
      const startTimeFromParams = this.validateTimeParam(this.$route.query.start);

      if (endTimeFromParams && endTimeFromParams > startTimeFromParams) {
        return endTimeFromParams;
      }
      return null;
    },
    defaultDistance(): number {
      const { distance } = this.$route.query;

      return parseInt(distance, 10) || 15;
    },
    defaultYearsExperience(): number | string {
      const experience = parseInt(this.$route.query.yearsExperience, 10);

      if (experience === 10) return '10+';
      return experience || 'Any';
    },
    defaultRate(): number {
      const rate = parseInt(this.$route.query.rate, 10);
      // do not let users type in negative numbers or 0 into params
      if (rate <= 0) {
        return 1;
      }

      return rate || 20;
    },
    defaultHasCar(): boolean {
      return this.$route.query.hasCar === 'true';
    },
    defaultLanguages(): Array<string> {
      let defaultLanguages = this.$route.query.languages;
      if (!!defaultLanguages && Array.isArray(defaultLanguages) === false) {
        defaultLanguages = Array.of(defaultLanguages);
      }
      return !defaultLanguages ? [] : defaultLanguages;
    },
    defaultBackgroundChecks(): Array<string> {
      const backgroundChecksParams = this.$route.query.backgroundChecks;
      if (backgroundChecksParams) {
        if (Array.isArray(backgroundChecksParams)) {
          return backgroundChecksParams;
        }
        return Array.of(backgroundChecksParams);
      }
      return [];
    },
    defaultAgeRanges(): Array<string> {
      const ageRangesParams = this.$route.query.ageRanges;
      if (ageRangesParams) {
        if (Array.isArray(ageRangesParams)) {
          return ageRangesParams;
        }
        return Array.of(ageRangesParams);
      }
      return [];
    },
    defaultRequirements(): Array<string> {
      const requirementMap = {
        hasCar: 'HAS_CAR',
        isComfortableWithPets: 'COMFORTABLE_WITH_PETS',
        hasMultiplesExperience: 'MULTIPLES_EXPERIENCE',
        hasSpecialNeedsExperience: 'SPECIAL_NEEDS_EXPERIENCE',
      };

      return Object.entries(requirementMap)
        .filter(([key]) => this.$route.query[key] === 'true')
        .map(([, value]) => value);
    },
    defaultResponsibilities(): Array<string> {
      const responsibilitiesParams = this.$route.query.responsibilities;
      if (responsibilitiesParams) {
        if (Array.isArray(responsibilitiesParams)) {
          return responsibilitiesParams;
        }
        return Array.of(responsibilitiesParams);
      }
      return [];
    },
    defaultLastLoginDays(): number {
      const lastLoginDayParam = this.$route.query.lastLoginDays;
      if (lastLoginDayParam) {
        return parseInt(lastLoginDayParam, 10);
      }
      return 42;
    },
    removeCareTypeFromQuery() {
      if (this.$route.query.careType) {
        const { careType: _careType, ...restQuery } = this.$route.query;
        this.$router.replace({ query: restQuery });
      }
    },
    resetForm() {
      this.isResetting = true;
      // Cancel any pending debounced submit
      if (this.debouncedAutoSubmit && this.debouncedAutoSubmit.cancel) {
        this.debouncedAutoSubmit.cancel();
      }
      this.zipcode = '';
      this.distance = 15;
      this.rate = 20;
      this.startDate = null;
      this.start = null;
      this.end = null;
      this.yearsExperience = 'Any';
      this.userEnteredZipcode = undefined;
      this.languages = [];
      this.keywords = '';
      this.backgroundChecks = [];
      this.ageRanges = [];
      this.requirements = [];
      this.responsibilities = [];
      this.lastLoginDays = 42;
      this.currentCare = 'Child care';

      // Clear all query parameters
      if (Object.keys(this.$route.query).length > 0) {
        this.$router.push({ query: {} }).then(() => {
          this.$nextTick(() => {
            this.isResetting = false;
          });
        });
      } else {
        this.isResetting = false;
      }
    },
    autoSubmitForm() {
      this.debouncedAutoSubmit();
    },
    handleTextFieldBlur() {
      if (this.isDesktop) {
        this.autoSubmitForm();
      }
    },
    handleResize() {
      this.isDesktop = window.innerWidth >= 768;
    },
  },
};
</script>

<style scoped>
.form__section {
  margin-top: 24px;
}
</style>
